import { useState } from "react";
import clsx from "clsx";
import { DataTable } from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { getCols } from "../../common/getCols.ts";
import { helpers } from "../util/helpers.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import type { View } from "../../common/types.ts";

type Tab = "history" | "season" | "records";

const num = (title: string, desc?: string) => ({
	title,
	desc,
	sortSequence: ["desc", "asc"] as const,
	sortType: "number" as const,
});
const text = (title: string) => ({ title, sortSequence: ["asc", "desc"] as const });

const STAT_LABELS: Record<string, string> = {
	h: "Hits",
	hr: "Home runs",
	rbi: "RBI",
	r: "Runs",
	sb: "Stolen bases",
	soPit: "Strikeouts (pitching)",
	w: "Wins (pitching)",
};

const record = (rec?: { wins?: number; won: number; lost: number }) =>
	rec ? `${rec.won}-${rec.lost}` : "";

const ip = (outs: number) => `${Math.floor(outs / 3)}.${outs % 3}`;

const SOURCE_TEXT = {
	recorded: "",
	boxScores: "Rebuilt from the box scores still saved.",
	missing:
		"No player stats for this round: box scores of older seasons are deleted (see the league's save old box scores setting), and per-round tallies only exist for games played since this feature was added.",
};

const Finals = ({
	detail,
	finalsMvps,
	finalsSummary,
	franchiseRecords,
	history,
	records,
}: View<"finals">) => {
	useTitleBar({ title: "Finals" });
	const [tab, setTab] = useState<Tab>("history");
	const [round, setRound] = useState<number | undefined>(undefined);

	const goSeason = (newSeason: number | "all") => {
		setTab("season");
		setRound(undefined);
		realtimeUpdate([], helpers.leagueUrl(["finals", newSeason]));
	};

	const tabs = (
		<ul className="nav nav-tabs mb-3">
			{(
				[
					["history", "Finals History"],
					["records", "Finals Records"],
					["season", "Stats by Round"],
				] as const
			).map(([key, label]) => (
				<li className="nav-item" key={key}>
					<button
						className={clsx("nav-link", { active: tab === key })}
						onClick={() => setTab(key)}
					>
						{label}
					</button>
				</li>
			))}
		</ul>
	);

	if (tab === "history") {
		return (
			<>
				{tabs}
				<DataTable
					cols={getCols([
						num("Season"),
						text("Champion"),
						text("Runner-up"),
						text("Series"),
					])}
					defaultSort={[0, "desc"]}
					name="Finals:History"
					pagination
					rows={history.map((row) => ({
						key: row.season,
						data: [
							{
								value: (
									<a
										href="#"
										onClick={(event) => {
											event.preventDefault();
											goSeason(row.season);
										}}
									>
										{row.season}
									</a>
								),
								sortValue: row.season,
							},
							`${row.champion.name} (#${row.champion.seed}, ${record(row.champion.record)})`,
							`${row.runnerUp.name} (#${row.runnerUp.seed}, ${record(row.runnerUp.record)})`,
							row.done ? row.result : `${row.result} (in progress)`,
						],
					}))}
				/>
			</>
		);
	}

	if (tab === "records") {
		const rows = records.stats.map((stat) => ({
			key: stat,
			data: [
				STAT_LABELS[stat] ?? stat,
				...(["game", "series", "career"] as const).map((kind) => {
					const rec = (records as any)[kind][stat];
					if (!rec) {
						return null;
					}
					return {
						value: (
							<>
								<b>{rec.value}</b>{" "}
								<a href={helpers.leagueUrl(["player", rec.pid])}>{rec.name}</a>
								{kind === "career"
									? ` (${rec.season} final${rec.season === 1 ? "" : "s"})`
									: ` (${rec.abbrev}, ${rec.season})`}
							</>
						),
						sortValue: rec.value,
					};
				}),
			],
		}));
		const covered = records.coveredSeasons;
		return (
			<>
				{tabs}
				<p>
					{finalsSummary.finals} finals played: {finalsSummary.sweeps} sweeps
					and {finalsSummary.fullLength} that went the distance.
				</p>

				<h2>Franchises</h2>
				<DataTable
					cols={getCols([
						text("Team"),
						num("Titles"),
						num("Finals"),
						num("Runner-up"),
						num("Sweeps"),
						num("Games W"),
						num("Games L"),
						num("Last Title"),
					])}
					defaultSort={[1, "desc"]}
					name="Finals:Franchises"
					rows={franchiseRecords.map((t) => ({
						key: t.tid,
						data: [t.name, t.titles, t.finals, t.runnerUp, t.sweeps, t.gamesWon, t.gamesLost, t.lastTitle ?? ""],
					}))}
				/>

				<h2 className="mt-3">Finals MVPs</h2>
				<DataTable
					cols={getCols([text("Player"), num("MVPs"), text("Seasons")])}
					defaultSort={[1, "desc"]}
					name="Finals:MVPs"
					pagination
					rows={finalsMvps.map((p) => ({
						key: p.pid,
						data: [
							<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>,
							p.count,
							[...p.seasons].sort((a, b) => a - b).join(", "),
						],
					}))}
				/>

				<h2 className="mt-3">Player records in the finals</h2>
				<p>
					Player stats split by round only exist for finals played since this
					was added
					{covered.length > 0 ? ` (${covered.join(", ")})` : ", so this fills in from the next finals on"}
					. Single game records also need the box scores, which are only kept
					for recent seasons.
				</p>
				<DataTable
					cols={getCols([text("Stat"), text("Single game"), text("Series"), text("Career")])}
					defaultSort={[0, "asc"]}
					name="Finals:Records"
					rows={rows}
				/>
			</>
		);
	}

	if (!detail) {
		return (
			<>
				{tabs}
				<p>No playoffs for this season.</p>
			</>
		);
	}

	const allSeasons = detail.season === "all";
	const selectedRound = round !== undefined && round < detail.rounds.length ? round : detail.rounds.length - 1;
	const r = detail.rounds[selectedRound]!;
	const batters = r.stats.filter((p) => p.pa > 0);
	const pitchers = r.stats.filter((p) => p.outs > 0);

	return (
		<>
			{tabs}
			<div className="mb-3">
				<select
					className="form-select w-auto d-inline-block"
					value={detail.season}
					onChange={(event) => goSeason(event.target.value === "all" ? "all" : Number(event.target.value))}
				>
					<option value="all">ALL — All seasons</option>
					{history.map((row) => (
						<option key={row.season} value={row.season}>
							{row.season}
						</option>
					))}
				</select>
			</div>

			{allSeasons ? (
				<p className="text-body-secondary">
					All seasons: one row per player and season in the selected round. Sort any column to find the record holders.
				</p>
			) : null}
			<ul className="nav nav-pills mb-2">
				{detail.rounds.map((rd) => (
					<li className="nav-item" key={rd.round}>
						<button
							className={clsx("nav-link", { active: selectedRound === rd.round })}
							onClick={() => setRound(rd.round)}
						>
							{rd.label}
						</button>
					</li>
				))}
			</ul>
			{r.source !== "recorded" ? (
				<p className="text-body-secondary">{SOURCE_TEXT[r.source]}</p>
			) : null}
			{allSeasons && r.missingSeasons.length > 0 ? (
				<p className="text-body-secondary">
					Coverage: {r.coveredSeasons.length} seasons with data; {r.missingSeasons.length} without saved round stats or complete box scores. Missing seasons are excluded, not treated as zero.
				</p>
			) : null}
			{r.stats.some((p) => p.pa > 0 && p.abKnown === false) ? (
				<p className="text-body-secondary">AB unavailable for some old records: the required components and complete box scores were not saved.</p>
			) : null}
			{r.highlights.batter || r.highlights.pitcher ? (
				<p>
					<b>Highlights:</b>{" "}
					{r.highlights.batter
						? `${r.highlights.batter.name}: ${r.highlights.batter.h} H${r.highlights.batter.abKnown === false ? " (AB unavailable)" : ` / ${r.highlights.batter.ab} AB`}, ${r.highlights.batter.hr} HR, ${r.highlights.batter.rbi} RBI`
						: null}
					{r.highlights.batter && r.highlights.pitcher ? " · " : null}
					{r.highlights.pitcher
						? `${r.highlights.pitcher.name}: ${ip(r.highlights.pitcher.outs)} IP, ${r.highlights.pitcher.soPit} K, ${r.highlights.pitcher.er} ER`
						: null}
				</p>
			) : null}
			{batters.length > 0 ? (
				<DataTable
					cols={getCols([
						text("Player"),
						...(allSeasons ? ["Season"] : []),
						"Team",
						num("G"),
						num("AB"),
						num("H"),
						num("2B"),
						num("3B"),
						num("HR"),
						num("GS", "Grand slams"),
						num("RBI"),
						num("BB"),
						num("SO"),
						num("SB"),
					])}
					defaultSort={[allSeasons ? 5 : 4, "desc"]}
					name={allSeasons ? "Finals:BattingAll" : "Finals:Batting"}
					rows={batters.map((p) => ({
						key: `${p.season}:${p.pid}:${p.tid}`,
						data: [
							<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>,
							...(allSeasons ? [p.season] : []),
							p.abbrev,
							p.gp,
							p.abKnown === false ? null : p.ab,
							p.h,
							p["2b"],
							p["3b"],
							p.hr,
							p.gsl,
							p.rbi,
							p.bb,
							p.so,
							p.sb,
						],
					}))}
				/>
			) : null}
			{pitchers.length > 0 ? (
				<DataTable
					cols={getCols([
						text("Pitcher"),
						...(allSeasons ? ["Season"] : []),
						"Team",
						num("G"),
						num("GS"),
						{ ...num("IP"), sortType: "number" },
						num("H"),
						num("ER"),
						num("BB"),
						num("K"),
						num("HR"),
						num("W"),
						num("L"),
						num("SV"),
					])}
					defaultSort={[allSeasons ? 5 : 4, "desc"]}
					name={allSeasons ? "Finals:PitchingAll" : "Finals:Pitching"}
					rows={pitchers.map((p) => ({
						key: `${p.season}:${p.pid}:${p.tid}`,
						data: [
							<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>,
							...(allSeasons ? [p.season] : []),
							p.abbrev,
							p.gpPit,
							p.gsPit,
							{ value: ip(p.outs), sortValue: p.outs },
							p.hPit,
							p.er,
							p.bbPit,
							p.soPit,
							p.hrPit,
							p.w,
							p.l,
							p.sv,
						],
					}))}
				/>
			) : null}
		</>
	);
};

export default Finals;
